
import React, { memo } from "react";
import { PieChart, Pie, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";

interface ProgramDataItem {
  name: string;
  value: number;
}

interface ProgramDistributionChartProps {
  data: ProgramDataItem[];
  loading: boolean;
}

const COLORS = ["#3D7069", "#071919", "#5C8D87", "#0F2E2E", "#89B0AC"];

// Performance optimized chart component with enhanced animations
const ProgramDistributionChart = memo(({ data, loading }: ProgramDistributionChartProps) => {
  // Determine if animations should be active based on user preference
  const isAnimationActive = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (loading) {
    return (
      <div className="h-[250px] w-full">
        <Skeleton className="h-full w-full rounded-full mx-auto" style={{ maxWidth: "80%" }} />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="h-[250px] flex items-center justify-center">
        <div className="text-center text-muted-foreground">
          No program data available
        </div>
      </div>
    );
  }

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    if (percent < 0.05) return null; // Don't render labels for very small slices
    
    return (
      <text 
        x={x} 
        y={y} 
        fill="#ffffff" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="h-[250px] w-full animate-fade-in">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={90}
            fill="#8884d8"
            dataKey="value"
            isAnimationActive={isAnimationActive}
            animationDuration={800}
            animationBegin={0}
            animationEasing="ease-out"
            className="hover-scale"
          >
            {data.map((_, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={COLORS[index % COLORS.length]}
                className="hover:opacity-90 transition-opacity"
              />
            ))}
          </Pie>
          <Tooltip 
            formatter={(value) => [`${value} enrollments`, ""]}
            contentStyle={{ 
              borderRadius: '8px', 
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
});

ProgramDistributionChart.displayName = 'ProgramDistributionChart';

export default ProgramDistributionChart;
